import type { __internal__ } from "@foxitsoftware/foxit-pdf-sdk-for-web-library/lib";
import type { StateCreator } from "zustand";

import type { PDFViewMode } from "../../helper-types";
import type { PDFViewCtrl } from "../../loaders/foxit-pdf-view-ctrl";
import type { PDFControllerSlice } from "./types";

import { useSuspenseFoxitPDFViewCtrlLoader } from "../../loaders/foxit-pdf-view-ctrl";
import { mapHighlightCoordsToFoxitRect } from "../../utils";
import { usePDFConfig } from "../pdf-config-provider";
import {
  DEFAULT_SCALE,
  MAX_SCALE,
  MIN_SCALE,
  ROTATE_0,
  ROTATE_90,
  ROTATE_180,
  ROTATE_270,
  SINGLE_PAGE_VIEW_PAGE_COUNT_THRESHOLD,
} from "./const";
import { createPdfViewer } from "./create-pdf-viewer";
import { pdfViewerCustomEvents } from "./types";

type PDFDoc = NonNullable<
  ReturnType<PDFViewCtrl.PDFViewer["getCurrentPDFDoc"]>
>;

export const usePDFControllerSlice = () => {
  const { data: PDFViewCtrlClass } = useSuspenseFoxitPDFViewCtrlLoader();
  const { libPath, jrConfig } = usePDFConfig();

  const safeGet = (get: () => PDFControllerSlice) => {
    const { pdfViewer, ...rest } = get();

    if (!pdfViewer) {
      throw new Error("PDF Viewer is not initialized yet");
    }
    return { pdfViewer, ...rest };
  };

  const getPDFDoc = (pdfViewer: PDFViewCtrl.PDFViewer) => {
    const pdfDoc = pdfViewer.getCurrentPDFDoc();
    if (!pdfDoc) {
      throw new Error("PDF Doc in not initialized yet");
    }
    return pdfDoc;
  };

  const getPDFDocRender = (pdfViewer: PDFViewCtrl.PDFViewer) => {
    const pdfDocRender = pdfViewer.getPDFDocRender();
    if (!pdfDocRender) {
      throw new Error("PDF DocRender is not initialized yet");
    }
    return pdfDocRender;
  };

  function assertPDFViewCtrlAvailable(
    pdfViewCtrl: typeof PDFViewCtrlClass,
  ): asserts pdfViewCtrl {
    if (!pdfViewCtrl) {
      throw new Error("PDFViewCtrl or worker is not available yet");
    }
  }

  const getTextRect = async (
    pdfDoc: PDFDoc,
    pageIndex: number,
    text: string,
  ) => {
    assertPDFViewCtrlAvailable(PDFViewCtrlClass);
    const { Search_Flag } = PDFViewCtrlClass.PDF.constant;
    const page = await pdfDoc.getPageByIndex(pageIndex);

    const searchResult = await page.getTextSearch(
      text,
      Search_Flag.consecutively,
    );

    const match = await searchResult.findNext();
    if (!match) {
      return null;
    }

    const rects = match.getRects();
    if (!rects[0]) {
      return null;
    }

    return rects[0];
  };

  const highlightTextOnPage = async (
    pdfDoc: PDFDoc,
    pageIndex: number,
    stringsToHighlight: string[],
  ) => {
    assertPDFViewCtrlAvailable(PDFViewCtrlClass);

    const { Annot_Flags, Annot_Type } = PDFViewCtrlClass.PDF.annots.constant;
    const { Search_Flag } = PDFViewCtrlClass.PDF.constant;
    type TextSearchMatch = PDFViewCtrl.PDF.search.TextSearchMatch;

    const page = await pdfDoc.getPageByIndex(pageIndex);

    for (const text of stringsToHighlight) {
      const searchResult = await page.getTextSearch(
        text,
        Search_Flag.consecutively,
      );

      const annotations = [];

      let match: TextSearchMatch | null;
      while ((match = await searchResult.findNext())) {
        const rects = match.getRects();
        for (const rect of rects) {
          const annotation = await page.addAnnot({
            flags: Annot_Flags.readOnly,
            type: Annot_Type.highlight,
            rect,
            coords: [rect],
            opacity: 0.5,
            date: new Date().getTime(),
            color: "eeff00",
          });

          annotations.push(...annotation);
        }
      }
      await searchResult.destroy();

      return annotations;
    }
  };

  const loadBookmarks = async (pdfViewer: __internal__.PDFViewer) => {
    const bookmarkDataService = pdfViewer.getBookmarkDataService();
    return bookmarkDataService.getFirstLevelBookmarks();
  };

  const createPDFControlsSlice: StateCreator<
    PDFControllerSlice,
    [],
    [],
    PDFControllerSlice
  > = (set, get) => ({
    pdfViewer: null,
    state: "closed",
    pdfUrl: null,
    fileName: null,
    currentPageIndex: null,
    scale: DEFAULT_SCALE,
    pageCount: null,
    error: null,
    bookmarks: null,
    id: null,
    viewMode: null,
    events: null,
    addEventListener: (eventName, listener) => {
      const { pdfViewer } = safeGet(get);
      if (!eventName) throw new Error("Event name is not defined");

      pdfViewer.getEventEmitter().addListener(eventName, listener);
    },
    removeEventListener: (eventName, listener) => {
      const { pdfViewer } = safeGet(get);
      if (!eventName) throw new Error("Event name is not defined");

      pdfViewer.getEventEmitter().removeListener(eventName, listener);
    },
    init: (el) => {
      const { pdfViewer: currentPdfViewer } = get();
      if (currentPdfViewer) {
        throw new Error("Cannot reinitialize existing PDF viewer");
      }
      if (!PDFViewCtrlClass) {
        throw new Error("PDFViewCtrlClass is not available yet");
      }
      const pdfViewer = createPdfViewer(PDFViewCtrlClass, jrConfig, libPath);
      pdfViewer.init(el);
      set({
        pdfViewer,
        events: {
          ...PDFViewCtrlClass.constants.ViewerEvents,
          ...pdfViewerCustomEvents,
        },
        state: "initialized",
      });
    },
    open: async (url, id) => {
      const { pdfViewer } = safeGet(get);

      try {
        const pdfDoc = await pdfViewer.openPDFByHttpRangeRequest(
          {
            range: { url },
          },
          { isRenderOnDocLoaded: true },
        );

        const pageCount = pdfDoc.getPageCount();
        const viewMode: PDFViewMode =
          pageCount >= SINGLE_PAGE_VIEW_PAGE_COUNT_THRESHOLD
            ? "single-page-view-mode"
            : "continuous-view-mode";
        pdfViewer.getViewModeManager().switchTo(viewMode);

        const bookmarks = await loadBookmarks(pdfViewer);

        set({
          currentPageIndex: 0,
          pdfUrl: url,
          pageCount,
          bookmarks,
          viewMode,
          error: null,
          id,
          state: "opened",
        });

        pdfViewer
          .getEventEmitter()
          .emit(pdfViewerCustomEvents.pdfViewerOpenSuccess, { pageCount });
      } catch (e) {
        pdfViewer.destroy();

        set({
          error:
            "We couldn’t open the PDF file. Please check the file and try again.",
          pdfUrl: null,
          viewMode: null,
          pageCount: null,
          bookmarks: null,
          id: null,
          state: "error",
        });
      }
    },
    close: () => {
      const { pdfViewer } = get();
      if (pdfViewer) {
        pdfViewer.destroy();
      }

      set({
        currentPageIndex: null,
        pdfViewer: null,
        events: null,
        id: null,
        pdfUrl: null,
        viewMode: null,
        pageCount: null,
        error: null,
        bookmarks: null,
        state: "closed",
      });
    },

    nextPage: async () => {
      const { pageCount, pdfViewer } = safeGet(get);

      if (!pageCount) throw new Error("Page count is not defined");

      const pdfDocRender = getPDFDocRender(pdfViewer);

      const currentPageIndex = pdfDocRender?.getCurrentPageIndex();
      const nextPageIndex = Math.min(currentPageIndex + 1, pageCount - 1);
      if (nextPageIndex <= (pageCount ?? 0))
        await pdfDocRender?.goToPage(nextPageIndex);
    },
    prevPage: async () => {
      const { pdfViewer } = safeGet(get);
      const pdfDocRender = getPDFDocRender(pdfViewer);

      const currentPageIndex = pdfDocRender?.getCurrentPageIndex();
      const prevPageIndex = Math.max(currentPageIndex - 1, 0);
      if (prevPageIndex >= 0) await pdfDocRender?.goToPage(prevPageIndex);
    },
    getTextRect: async (pageIndex, text) => {
      const { pdfViewer } = safeGet(get);
      const pdfDoc = getPDFDoc(pdfViewer);

      return getTextRect(pdfDoc, pageIndex, text);
    },
    jumpToPage: async (pageIndex: number, offset) => {
      const { pdfViewer } = safeGet(get);
      const pdfDocRender = getPDFDocRender(pdfViewer);
      const currentPageIndex = pdfDocRender.getCurrentPageIndex();
      if (pageIndex === currentPageIndex && !offset) {
        return;
      }

      const pageCount = getPDFDoc(pdfViewer).getPageCount();
      if (pageIndex >= 0 && pageIndex <= pageCount) {
        if (offset) {
          await pdfDocRender.goToPage(pageIndex, offset, true);
        } else {
          await pdfDocRender.goToPage(pageIndex);
        }
      }
    },
    setCurrentPageIndex: (pageIndex: number) => {
      set({ currentPageIndex: pageIndex });
    },
    getPageThumbnail: async (pageIndex: number): Promise<HTMLImageElement> => {
      const { pdfViewer } = safeGet(get);
      const pdfDoc = getPDFDoc(pdfViewer);

      return (await pdfDoc.loadThumbnail({
        pageIndex,
        type: "image",
      })) as HTMLImageElement;
    },
    loadBookmarks: () => {
      const { pdfViewer } = safeGet(get);

      return loadBookmarks(pdfViewer);
    },
    rotate: async (direction) => {
      const { pdfViewer } = safeGet(get);

      const currentPageIndex = getPDFDocRender(pdfViewer).getCurrentPageIndex();

      if (currentPageIndex >= 0) {
        const pdfDoc = getPDFDoc(pdfViewer);
        const page = await pdfDoc.getPageByIndex(currentPageIndex);

        const currentPageRotation = page.getRotation();

        let newRotation = -1;

        if (direction === "left") {
          switch (currentPageRotation) {
            case ROTATE_0:
              newRotation = ROTATE_270;
              break;
            case ROTATE_90:
              newRotation = ROTATE_0;
              break;
            case ROTATE_180:
              newRotation = ROTATE_90;
              break;
            case ROTATE_270:
              newRotation = ROTATE_180;
              break;
            default:
              newRotation = currentPageRotation;
              break;
          }
        } else if (direction === "right") {
          switch (currentPageRotation) {
            case ROTATE_0:
              newRotation = ROTATE_90;
              break;
            case ROTATE_90:
              newRotation = ROTATE_180;
              break;
            case ROTATE_180:
              newRotation = ROTATE_270;
              break;
            case ROTATE_270:
              newRotation = ROTATE_0;
              break;
            default:
              newRotation = currentPageIndex;
              break;
          }
        }

        page.setRotation(newRotation);
      }
    },
    zoom: async (step) => {
      const { pdfViewer } = safeGet(get);
      const pdfDocRender = getPDFDocRender(pdfViewer);

      const currentScale = await pdfDocRender.getScale();

      const isRectWithScale = (rect: object): rect is { scale: number } =>
        rect && Object.hasOwn(rect, "scale");

      const roundOneDecimal = (num: number) => Math.round(num * 10) / 10;

      switch (currentScale) {
        case "fitWidth":
        case "fitHeight": {
          const rect = pdfDocRender.getBoundingClientRects()?.[0];

          if (rect && isRectWithScale(rect)) {
            const newScale =
              typeof step === "number"
                ? roundOneDecimal(rect.scale + step)
                : step;

            if (
              (typeof newScale === "number" && newScale < MIN_SCALE) ||
              (typeof newScale === "number" && newScale > MAX_SCALE)
            )
              return;

            await pdfViewer.zoomTo(newScale);

            set({ scale: newScale });
            return;
          }
          break;
        }
        default: {
          const newScale =
            typeof step === "number" ? currentScale + step : step;

          if (
            (typeof newScale === "number" && newScale < MIN_SCALE) ||
            (typeof newScale === "number" && newScale > MAX_SCALE)
          )
            return;

          await pdfViewer.zoomTo(newScale);

          set({ scale: newScale });
        }
      }
    },
    highlightTextOnPage: async (
      pageIndex: number,
      stringsToHighlight: string[],
    ) => {
      const { pdfViewer } = safeGet(get);

      const pdfDoc = getPDFDoc(pdfViewer);

      const annotations = await highlightTextOnPage(
        pdfDoc,
        pageIndex,
        stringsToHighlight,
      );

      return annotations ?? [];
    },
    removeHighlights: async (highlights) => {
      for (const highlight of highlights) {
        const page = highlight.getPage();
        await page.removeAnnotById(highlight.getUniqueID());
      }
    },
    removeHighlightsOnPage: async (pageIndex: number) => {
      const { pdfViewer } = safeGet(get);
      const pdfDoc = getPDFDoc(pdfViewer);
      const page = await pdfDoc.getPageByIndex(pageIndex);
      await page.removeAllAnnot();
      return;
    },
    addHighlight: async (pageIndex, highlightCoords, options) => {
      const { pdfViewer } = safeGet(get);

      const pdfDoc = getPDFDoc(pdfViewer);

      assertPDFViewCtrlAvailable(PDFViewCtrlClass);

      const { Annot_Type, Annot_Flags } = PDFViewCtrlClass.PDF.annots.constant;

      const firstCoords = highlightCoords[0];
      if (!firstCoords) {
        throw new Error(
          "highlightCoords array must contain at least one element",
        );
      }

      const rect = mapHighlightCoordsToFoxitRect(firstCoords);

      const page = await pdfDoc.getPageByIndex(pageIndex);

      const [firstHighlight, ...addedHighlights] = await page.addAnnot({
        type: Annot_Type.highlight,
        flags: Annot_Flags.readOnly,
        color: "0xD77870",
        rect: rect,
        noPopup: true,
        coords: highlightCoords.map(mapHighlightCoordsToFoxitRect),
        ...options,
      });

      if (!firstHighlight || addedHighlights.length !== 0) {
        throw new Error(
          "Expected addHighlight to create a single PDF annotation",
        );
      }

      return firstHighlight;
    },

    getPageDimensions: async (pageIndex) => {
      const { pdfViewer } = safeGet(get);
      const pdfDoc = getPDFDoc(pdfViewer);

      const page = await pdfDoc.getPageByIndex(pageIndex);
      if (!page) {
        throw new Error(`Page with index ${pageIndex} is not available`);
      }
      const pageWidth = page.getWidth();
      const pageHeight = page.getHeight();
      const rotation = page.getRotation();

      const isPageRotated = rotation === ROTATE_90 || rotation === ROTATE_270;
      if (isPageRotated) {
        return {
          width: pageHeight,
          height: pageWidth,
        };
      }
      return {
        width: pageWidth,
        height: pageHeight,
      };
    },

    setViewMode: (viewMode: PDFViewMode) => {
      const { pdfViewer } = safeGet(get);
      pdfViewer.getViewModeManager().switchTo(viewMode);
      set({ viewMode });
    },
  });

  return createPDFControlsSlice;
};
